import { ExternalLinkIcon } from '@radix-ui/react-icons';
import { Box, Flex, IconButton, SegmentedControl } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { SuperAdminIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { GameDataPlayerSelector } from 'components/common/game-data-player-selector';
import { StatsApiVsManualToggle } from 'components/common/stats-api-vs-manual-toggle';
import { CommonTabs } from 'components/common/tabs';
import env from 'config';
import { MachineContext } from 'contexts/machine.context';
import { StatsApiManualFormMode } from 'enums/forms';
import { GDPlayerSelectorMode } from 'enums/game-data.enums';
import { t } from 'i18next';
import { DEFAULT_ACCEPT_BTN, IBaseDialog } from 'interfaces/i-dialogs';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { ERROR_MSGS } from 'lib_ts/enums/errors.enums';
import { PitchListOwner } from 'lib_ts/enums/pitch-list.enums';
import {
  NON_SAMPLE_LIST_TYPES,
  PITCHER_HANDS,
  PITCHER_POSITIONS,
  PitcherHand,
  PitcherPosition,
  ReferenceListType,
} from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { WithID } from 'lib_ts/interfaces/common/i-generics';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import {
  FOLDER_SEPARATOR,
  IPitchList,
  safeFolder,
} from 'lib_ts/interfaces/pitches/i-pitch-list';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PitchListsService } from 'services/pitch-lists.service';
import { useAuthStore } from 'stores/use-auth-store';
import useListLibraryStore from 'stores/use-list-library-store';

const MIN_MODEL_BUILDER_GROUPS = env.production ? 7 : 1;

enum TabKey {
  ListDetails = 'ListDetails',
  PlayerData = 'PlayerData',
  AdminConfig = 'AdminConfig',
}

export const getVisibilityBlurb = (parent?: string, machineID?: string) => {
  switch (parent) {
    case PitchListOwner.Team: {
      return t('pl.accessible-to-team');
    }

    case PitchListOwner.Machine: {
      return t('pl.accessible-to-x', { x: machineID || t('common.machine') });
    }

    case PitchListOwner.User: {
      return t('pl.accessible-to-user');
    }

    default: {
      return;
    }
  }
};

enum FolderMode {
  Existing = 'common.existing-folder',
  New = 'common.new-folder',
}

const MAX_NAME_LENGTH = 30;
const MAX_FOLDER_LENGTH = 60;
const MAX_DESC_LENGTH = 3000;

export interface IManageListDialogProps extends IBaseDialog {
  mode: 'create' | 'edit' | 'copy';
  // should also close the dialog when ready
  onCreated: (list: IPitchList) => void;
}

/** used for creating, copying, and editing pitch lists */
export const ManageListDialog = (props: IManageListDialogProps) => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const { machine } = useContext(MachineContext);

  const loading = useListLibraryStore((s) => s.loading);
  const lists = useListLibraryStore((s) => s.lists);
  const activeList = useListLibraryStore((s) => s.activeList);

  const parentOptions = useMemo(() => {
    const output: IOption[] = [
      {
        label: 'common.personal',
        value: PitchListOwner.User,
      },
    ];

    if (current.machine_lists) {
      output.push({
        label: 'common.machine',
        value: PitchListOwner.Machine,
      });
    }

    if (current.team_lists) {
      output.push({
        label: 'common.team',
        value: PitchListOwner.Team,
      });
    }

    return output;
  }, [current.machine_lists, current.team_lists]);

  const defaultParentDef = current.machine_lists
    ? PitchListOwner.Machine
    : PitchListOwner.User;

  const [formModel, setFormModel] = useState<Partial<IPitchList>>(
    (() => {
      switch (props.mode) {
        case 'create': {
          const defaultModel: Partial<IPitchList> = {
            _parent_def: defaultParentDef,
            name: `Pitch List #${lists.length + 1}`,
            folder: '',
          };

          return defaultModel;
        }

        case 'copy':
        case 'edit':
        default: {
          const safeModel: Partial<IPitchList> = { ...activeList };

          if (
            props.mode === 'copy' &&
            safeModel.name &&
            !safeModel.name.startsWith('Copy of ')
          ) {
            safeModel.name = `Copy of ${safeModel.name}`;
          }

          if (!safeModel.name) {
            safeModel.name = 'Unnamed';
          }

          if (!safeModel.folder) {
            safeModel.folder = '';
          }

          if (!safeModel._parent_def) {
            safeModel._parent_def = defaultParentDef;
          }

          return safeModel;
        }
      }
    })()
  );

  const mergeModel = (value: Partial<IPitchList>) =>
    setFormModel((prev) => ({
      ...prev,
      ...value,
    }));

  const [folderMode, setFolderMode] = useState(FolderMode.Existing);
  const [tempParentDef, setTempParentDef] = useState<PitchListOwner>(
    activeList?._parent_def ?? defaultParentDef
  );

  // reset _parent_id whenever parent def changes
  useEffect(() => {
    if (!formModel._parent_id) {
      return;
    }

    if (tempParentDef === formModel._parent_def) {
      return;
    }

    mergeModel({ _parent_id: undefined });
  }, [formModel, tempParentDef]);

  const existingFolderOptions = useMemo(() => {
    const sameParent = lists.filter((l) => l._parent_def === tempParentDef);

    const uniqueFolders = ArrayHelper.unique(sameParent.map((l) => l.folder));

    const output: IOption[] = uniqueFolders.map((s) => {
      const o: IOption = {
        label: s,
        value: s,
      };

      return o;
    });

    return output;
  }, [lists, tempParentDef]);

  const dialogTitle = useMemo(() => {
    switch (props.mode) {
      case 'edit': {
        return t('common.edit-x', { x: t('common.list') });
      }

      case 'copy': {
        return t('common.duplicate-x', { x: t('common.list') });
      }

      case 'create': {
        return t('common.create-x', { x: t('common.list') });
      }

      default: {
        return t('common.manage-x', { x: t('common.list') });
      }
    }
  }, [props.mode]);

  const warningBlurb = useMemo(
    () => getVisibilityBlurb(formModel?._parent_def, machine.machineID),
    [formModel?._parent_def, machine.machineID]
  );

  const [tab, setTab] = useState(TabKey.ListDetails);
  const [formMode, setFormMode] = useState(() => {
    if (!current.mlb_stats_api) {
      // user does not have access to Stats API
      return StatsApiManualFormMode.Manual;
    }

    const { mlb_repertoire, pitcher, team } = formModel;

    // user has access to Stats API
    const isManual = !mlb_repertoire && (!!pitcher || !!team);

    if (isManual) {
      // i.e. existing data was input manually
      return StatsApiManualFormMode.Manual;
    }

    return StatsApiManualFormMode.StatsAPI;
  });

  const isRepertoire = !!formModel.mlb_repertoire;

  // MARK: CRUD callbacks
  const updateList = useCallback(
    async (payload: Partial<IPitchList>): Promise<void> => {
      try {
        useListLibraryStore.setState({ loading: true });

        const result = await PitchListsService.getInstance()
          .updateOne(payload)
          .finally(() => {
            useListLibraryStore.setState({ loading: false });
          });

        if (!result.success) {
          NotifyHelper.warning({
            message_md:
              result.error ??
              `There was an error updating your pitch list. ${ERROR_MSGS.CONTACT_SUPPORT}`,
          });
          return;
        }

        const updated = result.data as IPitchList;

        if (!updated) {
          NotifyHelper.error({
            message_md: 'Server responded with an empty result.',
          });
          return;
        }

        // replace in rawLists => lists
        useListLibraryStore.setState((prev) => ({
          rawLists: prev.rawLists.map((l) =>
            l._id === updated._id ? updated : l
          ),
        }));

        NotifyHelper.success({
          message_md: 'Pitch list updated!',
        });

        props.onCreated(updated);
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md: `There was an unexpected error updating your pitch list. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
      }
    },
    [props.onCreated]
  );

  const createList = useCallback(
    async (payload: Partial<IPitchList>): Promise<void> => {
      try {
        useListLibraryStore.setState({ loading: true });

        const newList = await PitchListsService.getInstance()
          .postList(payload)
          .finally(() => useListLibraryStore.setState({ loading: false }));

        if (!newList) {
          throw new Error('Failed to create pitch list');
        }

        useListLibraryStore.setState((prev) => ({
          rawLists: [...prev.rawLists, newList],
        }));

        NotifyHelper.success({
          message_md: `Pitch list "${newList.name}" created!`,
        });

        props.onCreated(newList);
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was an error creating your pitch list. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
      }
    },
    []
  );

  const copyList = useCallback(
    async (payload: WithID<Partial<IPitchList>>): Promise<void> => {
      try {
        useListLibraryStore.setState({ loading: true });

        const newList = await PitchListsService.getInstance()
          .copyOne(payload)
          .finally(() => useListLibraryStore.setState({ loading: false }));

        if (!newList) {
          throw new Error('Failed to copy pitch list');
        }

        useListLibraryStore.setState((prev) => ({
          rawLists: [...prev.rawLists, newList],
        }));

        NotifyHelper.success({
          message_md: `Pitch list "${newList.name}" copied!`,
        });

        props.onCreated(newList);
      } catch (e) {
        console.error(e);

        NotifyHelper.error({
          message_md:
            e instanceof Error
              ? e.message
              : `There was an error copying your pitch list. ${ERROR_MSGS.CONTACT_SUPPORT}`,
        });
      }
    },
    []
  );

  // MARK: render
  return (
    <ErrorBoundary identifier="ManageListDialog">
      <CommonDialog
        identifier={props.identifier}
        loading={loading}
        width={RADIX.DIALOG.WIDTH.MD}
        title={dialogTitle}
        content={
          <CommonTabs
            gap="2"
            tabs={[
              {
                value: TabKey.ListDetails,
                label: 'List Details',
                content: (
                  <CommonFormGrid columns={1}>
                    <CommonTextInput
                      id="manage-list-name"
                      label="common.name"
                      name="name"
                      value={formModel.name}
                      disabled={loading}
                      placeholder="Type in a list name"
                      maxLength={MAX_NAME_LENGTH}
                      onChange={(v) =>
                        mergeModel({
                          name: v,
                        })
                      }
                    />

                    <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
                      <CommonInputLabel
                        name="manage-list-folder-path"
                        label="Select Folder / Path"
                      />

                      <SegmentedControl.Root
                        value={folderMode}
                        onValueChange={(v) => setFolderMode(v as FolderMode)}
                      >
                        {Object.values(FolderMode).map((o, i) => (
                          <SegmentedControl.Item key={`mode-${i}`} value={o}>
                            {t(o)}
                          </SegmentedControl.Item>
                        ))}
                      </SegmentedControl.Root>

                      <CommonInputLabel name="path-name" label="Path Name" />
                      {folderMode === FolderMode.Existing && (
                        <CommonSearchInput
                          id="existing-folder"
                          name="search-existing-folder"
                          options={existingFolderOptions}
                          values={formModel.folder ? [formModel.folder] : []}
                          onChange={(v) =>
                            mergeModel({
                              folder: v[0],
                            })
                          }
                          disabled={loading}
                          optional
                        />
                      )}

                      {folderMode === FolderMode.New && (
                        <CommonTextInput
                          id="manage-list-folder"
                          name="folder"
                          value={formModel.folder}
                          disabled={loading}
                          placeholder="Type in a folder or path (optional)"
                          maxLength={MAX_FOLDER_LENGTH}
                          onChange={(v) =>
                            mergeModel({
                              folder: v,
                            })
                          }
                          hint_md={`e.g. "Team A" or "Practice${FOLDER_SEPARATOR}Warm-Up"`}
                        />
                      )}
                    </Flex>

                    {tempParentDef && (
                      <CommonSelectInput
                        id="manage-list-visibility"
                        name="_parent_def"
                        label="common.visibility"
                        options={parentOptions}
                        value={tempParentDef}
                        onChange={(v) => setTempParentDef(v as PitchListOwner)}
                        disabled={loading}
                        hint_md={warningBlurb}
                        skipSort
                      />
                    )}
                  </CommonFormGrid>
                ),
              },
              {
                label: 'Player Data',
                value: TabKey.PlayerData,
                content: (
                  <CommonFormGrid columns={1}>
                    <StatsApiVsManualToggle
                      mode={formMode}
                      onChange={(v) => setFormMode(v)}
                    />

                    {formMode === StatsApiManualFormMode.Manual && (
                      <>
                        <CommonTextInput
                          id="manage-pitcher-name"
                          label="common.pitcher"
                          name="pitcher"
                          rightSlot={
                            isRepertoire ? (
                              <IconButton
                                size="1"
                                className="btn-floating"
                                color={RADIX.COLOR.NEUTRAL}
                                variant={RADIX.BUTTON.VARIANT.BORDERLESS}
                                title="View on MLB.com"
                                onClick={() => {
                                  if (!formModel.mlb_repertoire?.playerPk) {
                                    return;
                                  }

                                  window.open(
                                    `https://www.mlb.com/player/${formModel.mlb_repertoire.playerPk}`,
                                    '_blank'
                                  );
                                }}
                              >
                                <ExternalLinkIcon />
                              </IconButton>
                            ) : undefined
                          }
                          value={formModel.pitcher || ''}
                          onChange={(v) =>
                            mergeModel({
                              pitcher: v,
                              mlb_repertoire: null, // changing details via manual entry should clear Stats API selection
                            })
                          }
                        />

                        <CommonTextInput
                          id="manage-team-name"
                          label="common.team"
                          name="team"
                          value={formModel.team || ''}
                          onChange={(v) =>
                            mergeModel({
                              team: v,
                              mlb_repertoire: null, // changing details via manual entry should clear Stats API selection
                            })
                          }
                        />

                        <CommonTextInput
                          id="manage-customID"
                          name="customID"
                          label="hitters.custom-id"
                          value={formModel.customID ?? ''}
                          placeholder={
                            t('common.type-a-unique-value') as string
                          }
                          onChange={(v) =>
                            mergeModel({
                              customID: v,
                              mlb_repertoire: null, // changing details via manual entry should clear Stats API selection
                            })
                          }
                        />

                        <CommonSelectInput
                          id="manage-hand"
                          name="hand"
                          label="common.handedness"
                          options={PITCHER_HANDS}
                          value={formModel.hand || ''}
                          onChange={(v) =>
                            mergeModel({
                              hand: v as PitcherHand,
                            })
                          }
                          disabled={isRepertoire}
                          optional
                        />
                      </>
                    )}

                    {formMode === StatsApiManualFormMode.StatsAPI && (
                      <Box>
                        <GameDataPlayerSelector
                          disabled={loading}
                          mode={GDPlayerSelectorMode.Pitcher}
                          model={formModel.mlb_repertoire}
                          onChangeModel={(player) => {
                            if (player) {
                              mergeModel({
                                mlb_repertoire: {
                                  season: player.season,
                                  sportId: player.sportId,
                                  playerPk: player.playerPk,
                                },
                                teamPk: player._mx_team_details?.teamPk,
                                pitcher: player.name,
                                team: player._mx_team_details?.name || '',
                                hand: player.pitchHand,
                                // only set list name if it's currently empty
                                name: formModel.name
                                  ? formModel.name
                                  : `${player.name} (${player.season})`,
                              });
                              return;
                            }

                            mergeModel({
                              mlb_repertoire: null,
                              teamPk: null,
                              pitcher: null,
                              team: null,
                              hand: null,
                            });
                          }}
                          autoClose
                        />
                      </Box>
                    )}

                    <CommonSelectInput
                      id="manage-position"
                      name="position"
                      label="common.position"
                      options={PITCHER_POSITIONS}
                      value={formModel.position}
                      onChange={(v) =>
                        mergeModel({
                          position: v as PitcherPosition,
                        })
                      }
                      optional
                      skipSort
                    />
                  </CommonFormGrid>
                ),
              },
              {
                labelIcon: <SuperAdminIcon />,
                invisible: current.role !== UserRole.admin,
                label: 'Admin Config',
                value: TabKey.AdminConfig,
                content: (
                  <CommonFormGrid columns={1}>
                    {userRoleFeatureFlags.manage.pitchListType && (
                      <CommonSelectInput
                        id="manage-list-ref-type"
                        name="reference-type"
                        label="common.sub-type"
                        options={Object.values(ReferenceListType).map((s) => ({
                          label: s,
                          value: s,
                        }))}
                        value={formModel.reference_type}
                        onChange={(v) =>
                          mergeModel({
                            reference_type: v as ReferenceListType,
                          })
                        }
                        disabled={loading}
                        labelOptional
                        optional
                      />
                    )}

                    {userRoleFeatureFlags.manage.pitchListType &&
                      formModel.reference_type ===
                        ReferenceListType.BallTypeCalibration && (
                        <CommonCallout
                          text={`Any reference lists for Ball Type Calibration must contain at least ${MIN_MODEL_BUILDER_GROUPS} unique pitch(es).`}
                          color={RADIX.COLOR.SUPER_ADMIN}
                        />
                      )}

                    {userRoleFeatureFlags.manage.pitchListType &&
                      formModel.reference_type &&
                      NON_SAMPLE_LIST_TYPES.includes(
                        formModel.reference_type
                      ) && (
                        <>
                          <CommonSelectInput
                            id="manage-list-live"
                            name="live"
                            label="Live"
                            options={[
                              { label: 'Yes', value: 'true' },
                              { label: 'No', value: 'false' },
                            ]}
                            value={formModel.live?.toString()}
                            onOptionalBooleanChange={(v) =>
                              mergeModel({ live: v })
                            }
                            disabled={loading}
                            hint_md="For reference lists, this will determine whether it is usable (e.g. for BTC or RT)."
                            optional
                          />
                        </>
                      )}
                    {props.mode === 'copy' && (
                      <CommonTextareaInput
                        id="manage-list-description"
                        label="common.description"
                        name="description_md"
                        value={formModel.description_md}
                        disabled={loading}
                        placeholder="Describe how this list should be used..."
                        maxLength={MAX_DESC_LENGTH}
                        onChange={(v) => mergeModel({ description_md: v })}
                        hint_md="Markdown input is also accepted"
                        rows={8}
                      />
                    )}
                  </CommonFormGrid>
                ),
              },
            ]}
            value={tab}
            onValueChange={(v) => setTab(v as TabKey)}
          />
        }
        buttons={[
          {
            ...DEFAULT_ACCEPT_BTN,
            onClick: async () => {
              if (!formModel?.name?.trim()) {
                NotifyHelper.error({
                  message_md: t('common.check-inputs-msg'),
                });
                return;
              }

              const payload: Partial<IPitchList> = {
                ...formModel,
                folder: safeFolder(formModel.folder),
                /** parent id will be set based on def, but via the server to ensure consistency */
                _parent_def: tempParentDef,
              };

              if (props.mode === 'edit' || props.mode === 'copy') {
                payload._id = activeList?._id;
              }

              switch (props.mode) {
                case 'create': {
                  createList(payload);
                  break;
                }

                case 'edit': {
                  updateList(payload);
                  break;
                }

                case 'copy': {
                  if (!payload?._id) {
                    NotifyHelper.error({
                      message_md: `Original list must be provided for copying. ${ERROR_MSGS.CONTACT_SUPPORT}`,
                    });
                    return;
                  }

                  copyList({
                    ...payload,
                    _id: payload._id,
                  });
                  break;
                }
              }
            },
          },
        ]}
        onClose={props.onClose}
      />
    </ErrorBoundary>
  );
};
